import { useQueries, useQuery } from '@tanstack/react-query'
import { monadClient } from '../../api/client'
import { tenantSlug } from '../../api/http'
import { attributesQuery, typeQuery } from '../../api/typeMetadata'
import { OP_REGISTRY } from '../operators'
import { registerWidget, type CellContentProps } from '../registry'

/**
 * `record-filter` — a cell whose value is a Poiesis filter clause over another type (a member's access:
 * "these sites and what hangs off them"). The kind's attribute names the type the filter is over in
 * `config.filter_target`. Read-only here: the cell is written by the verb its attribute serves, through the
 * verb form's FilterBar.
 *
 * The read form says it in words — "Country › Code is one of DK, or Site is one of Netto Ry, Netto Viby" —
 * attribute names from the served metadata, records by their kennings (the chips seam), never a raw id.
 */

export type FilterValue =
  | { attribute: string; op: string; value?: unknown }
  | { and: FilterValue[] }
  | { or: FilterValue[] }

/** The wire ops a filter clause may carry, said in words. The UI operator labels (OP_REGISTRY) are reused
 *  where the wire op names one of them; the wire-only ops (in, not_in, is_set, …) are said here. */
const WIRE_PHRASE: Record<string, string> = {
  in: 'is one of', not_in: 'is none of', ne: OP_REGISTRY.neq.label, is_set: OP_REGISTRY['non-empty'].label,
  is_not_set: OP_REGISTRY.empty.label,
}
const phrase = (op: string) => WIRE_PHRASE[op] ?? OP_REGISTRY[op]?.label ?? op

/** The record's own id, the path a filter names a record by. */
export const RECORD_ID = 'record_id'

export interface FilterNames {
  /** "country_ref.code" -> "Country › Code"; the record id path -> the type's singular name. */
  path: (attributePath: string) => string
  /** A record id -> its kenning, when known. */
  record: (id: string) => string | undefined
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function valueWords(value: unknown, names: FilterNames): string {
  const list = Array.isArray(value) ? value : [value]
  if (list.length === 0) return 'nothing'
  return list.map((v) => {
    const s = String(v)
    return UUID_RE.test(s) ? (names.record(s) ?? '…') : s
  }).join(', ')
}

/** The clause in words. Pure: names come in resolved. */
export function describeFilter(clause: FilterValue, names: FilterNames): string {
  if ('and' in clause) return clause.and.map((c) => describeFilter(c, names)).join(', and ')
  if ('or' in clause) return clause.or.map((c) => describeFilter(c, names)).join(', or ')
  const subject = names.path(clause.attribute)
  if (clause.op === 'in' && Array.isArray(clause.value) && clause.value.length === 0) return `${subject}: none`
  const needsValue = !['is_set', 'is_not_set', 'empty', 'non-empty'].includes(clause.op)
  return needsValue ? `${subject} ${phrase(clause.op)} ${valueWords(clause.value, names)}` : `${subject} ${phrase(clause.op)}`
}

/** Every record id and attribute path a clause names (what must be resolved to say it). */
export function filterParts(clause: unknown): { paths: string[]; recordIds: string[] } {
  const paths = new Set<string>()
  const ids = new Set<string>()
  const walk = (c: unknown) => {
    if (!c || typeof c !== 'object') return
    const n = c as Record<string, unknown>
    if (Array.isArray(n.and)) n.and.forEach(walk)
    else if (Array.isArray(n.or)) n.or.forEach(walk)
    else if (typeof n.attribute === 'string') {
      paths.add(n.attribute)
      for (const v of Array.isArray(n.value) ? n.value : [n.value]) if (typeof v === 'string' && UUID_RE.test(v)) ids.add(v)
    }
  }
  walk(clause)
  return { paths: [...paths], recordIds: [...ids] }
}

export function isFilterValue(value: unknown): value is FilterValue {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return Array.isArray(v.and) || Array.isArray(v.or) || (typeof v.attribute === 'string' && typeof v.op === 'string')
}

type Attr = { attribute_id: string; slug: string; name: string; config?: Record<string, unknown> | null }

/** The names a clause over `targetType` needs: hop names from the served attributes (two hops: a
 *  reference and its target's attribute), record kennings from the chips seam. */
export function useFilterNames(slug: string, targetType: string | undefined, clause: unknown): FilterNames | null {
  const { recordIds, paths } = filterParts(clause)
  const type = useQuery({ ...typeQuery(slug, targetType ?? ''), enabled: !!targetType })
  const attrs = useQuery({ ...attributesQuery(slug, targetType ?? ''), enabled: !!targetType })
  const firstHops = (attrs.data ?? []) as Attr[]
  const secondTypes = [...new Set(paths.filter((p) => p.includes('.')).map((p) => {
    const a = firstHops.find((x) => x.slug === p.split('.')[0])
    return (a?.config?.target_type_id as string | undefined) ?? ''
  }).filter(Boolean))]
  const second = useQueries({ queries: secondTypes.map((t) => attributesQuery(slug, t)) })
  const chips = useQuery({
    queryKey: ['chips', slug, ...[...recordIds].sort()], enabled: recordIds.length > 0,
    queryFn: () => monadClient.getChips(slug, recordIds), staleTime: 60_000,
  })
  if (!targetType || type.isPending || attrs.isPending) return null
  const typeName = (type.data as { singular_noun?: string; name?: string } | undefined)
  const own = typeName?.singular_noun ?? typeName?.name ?? targetType
  const secondAttrs = new Map(secondTypes.map((t, i) => [t, (second[i]?.data ?? []) as Attr[]]))
  return {
    path: (p) => {
      if (p === RECORD_ID) return own
      const [head, ...rest] = p.split('.')
      const first = firstHops.find((x) => x.slug === head)
      const names = [first?.name ?? head]
      if (rest.length) {
        const pool = secondAttrs.get((first?.config?.target_type_id as string | undefined) ?? '') ?? []
        names.push(...rest.map((r) => pool.find((x) => x.slug === r)?.name ?? r))
      }
      return names.join(' › ')
    },
    record: (id) => chips.data?.chips?.[id]?.kenning,
  }
}

/** What an empty filter means: no filter of the record's own — what it may see is decided elsewhere. */
export const NO_FILTER_REASON = 'No filter of its own: the role decides'

export function RecordFilterContent({ value, config }: CellContentProps) {
  const target = (config?.filter_target as string | undefined)
  const clause = isFilterValue(value) ? value : null
  const names = useFilterNames(tenantSlug(), target, clause)
  if (value == null) {
    return <span className="text-muted-foreground" title={NO_FILTER_REASON} data-testid="record-filter-empty">— <span className="text-xs">{NO_FILTER_REASON}</span></span>
  }
  if (!clause) return <span className="text-muted-foreground" title="This value is not a filter clause">— <span className="text-xs">not a filter</span></span>
  return <span className="text-sm" data-testid="record-filter-read">{names ? describeFilter(clause, names) : '…'}</span>
}

registerWidget('record-filter', { read: RecordFilterContent, defaultWidth: 280 })
